import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { theme } from '../../../theme/theme';
import { IGem, IGemAiResponse } from '../../../redux/gem/gemSlice';
import { AiStatus } from '../../../utils/enums/gem-ai-status.enum';
import { colors } from '../../V1/FilterComponent/GemFilterModal/Colors';
import { GemColor } from '../../../utils/enums/gem-color.enum';

interface GemAIInferenceProps {
  gem: IGem | null;
}

function GemAIInference({ gem }: GemAIInferenceProps) {
  const expandedRowColumns = ['Primary Color', 'Secondary Color', 'Special Color Descriptor'];
  const expandedRowValues = ['colorPrediction', 'secondaryColorPrediction', 'specialColorPrediction'];

  const gemId = gem?.is_scanned;

  const renderColorDot = (value: any) => {
    const colorMatch = colors.find((color) => color.name === value);
    return colorMatch ? (
      <Box
        component="span"
        data-testid="color-dot"
        sx={{
          display: 'inline-block',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: colorMatch.code,
          marginRight: '8px',
          verticalAlign: 'middle',
          marginTop: '5px',
          border: colorMatch.name === GemColor.White ? `1px solid ${theme.palette.common.white}` : 'none',
        }}
      />
    ) : null;
  };

  const renderSpecialDescriptor = (value: any) => (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 5px',
        borderRadius: '5px',
        border: `1px solid ${theme.palette.shades.grayBorder}`,
        backgroundColor: 'rgba(51, 72, 255, 0.04)',
        width: 'fit-content',
        maxWidth: '90%',
      }}
    >
      <Typography
        variant="textBaseLg500"
        color="black.dark"
        sx={{
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const renderAITag = () => (
    <Box
      component="span"
      sx={{
        marginLeft: '5px',
        display: 'inline-block',
        padding: '0px 8px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        borderRadius: '5px',
        background: '#6F7C8E',
      }}
    >
      <Typography
        variant="textXS"
        sx={{
          color: '#F6F7F9',
          fontWeight: 700,
        }}
      >
        AI
      </Typography>
    </Box>
  );

  const gemWithFallbacks: IGemAiResponse = {
    colorPrediction: gem?.aiResponse?.colorPrediction ?? 'N/A',
    secondaryColorPrediction: gem?.aiResponse?.secondaryColorPrediction ?? 'N/A',
    specialColorPrediction: gem?.aiResponse?.specialColorPrediction ?? 'N/A',
    responseId: gem?.aiResponse?.responseId ?? 'N/A',
    intensityPrediction: gem?.aiResponse?.intensityPrediction ?? 'N/A',
    colorProbability: gem?.aiResponse?.colorProbability ?? 0,
    uginId: gem?.aiResponse?.uginId ?? 'N/A',
  };

  const getGemAIColumnValue = (gemAi: IGemAiResponse, key: string): any => gemAi[key as keyof IGemAiResponse];

  const renderGemAnalysisStatus = (text: string, showSpinner: boolean): any => (
    <Grid
      p={3}
      display="flex"
      flexDirection="column"
      borderRadius="0 0px 4px 4px"
      border={`1px solid ${theme.palette.shades.grayBorder}`}
      gap={3}
      borderTop={gemId ? 'none' : undefined}
    >
      <Box display="flex" alignItems="center" justifyContent="center" mt={2} gap={1}>
        {showSpinner && (
          <Box
            data-testid="spinner"
            sx={{
              position: 'relative',
              width: '30px',
              height: '30px',
              border: '3px solid #EBEDFF',
              borderRadius: '50%',
              borderTop: '3px solid #3348FF',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        )}
        <Typography variant="textBase500" color="#14181F" marginLeft="5px">
          {text}
        </Typography>
      </Box>
    </Grid>
  );

  if (gem?.aiStatus === AiStatus.PENDING || (gem?.aiStatus === AiStatus.IN_PROGRESS && gem?.aiTryAttempts === 1)) {
    const renderData = { text: 'Analysis Under Progress...', showSpinner: true };
    return renderGemAnalysisStatus(renderData.text, renderData.showSpinner);
  }
  if (gem?.aiStatus === AiStatus.FAILED) {
    const renderData = { text: 'Analysis Failed', showSpinner: false };
    return renderGemAnalysisStatus(renderData.text, renderData.showSpinner);
  }
  if (gem?.aiStatus === AiStatus.IN_PROGRESS && gem.aiTryAttempts && gem.aiTryAttempts > 1) {
    const renderData = { text: 'Analysis Retrying', showSpinner: true };
    return renderGemAnalysisStatus(renderData.text, renderData.showSpinner);
  }
  if (gem) {
    return (
      <Grid
        p={3}
        display="flex"
        flexDirection="column"
        borderRadius="0 0px 4px 4px"
        border={`1px solid ${theme.palette.shades.grayBorder}`}
        gap={3}
        borderTop={gemId ? 'none' : undefined}
      >
        <Grid
          display="flex"
          flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
          gap={3}
          width="100%"
          alignItems="center"
        >
          <Grid container rowGap={3}>
            {expandedRowColumns.map((column, i) => {
              const value = getGemAIColumnValue(gemWithFallbacks, expandedRowValues[i]);

              return (
                <Grid
                  item
                  key={column}
                  display="flex"
                  flexDirection="column"
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={2}
                  sx={{
                    flex: '0 0 auto',
                    maxWidth: 'fit-content',
                    backgroundColor: 'transparent',
                    padding: '0',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                >
                  {(() => {
                    if (column === 'Special Color Descriptor') {
                      return renderSpecialDescriptor(value);
                    }
                    return (
                      <Typography
                        variant="textBase500"
                        sx={{
                          display: 'inline-flex',
                          borderRadius: '0 5px 0 0',
                          whiteSpace: 'nowrap',
                          maxWidth: '100px',
                          wordBreak: 'break-word',
                          color: 'inherit',
                          backgroundColor: 'transparent',
                        }}
                      >
                        {(column === 'Primary Color' || column === 'Secondary Color') && renderColorDot(value)}
                        {value}
                      </Typography>
                    );
                  })()}

                  <Typography
                    variant="textXS"
                    sx={{
                      display: 'inline-flex',
                      whiteSpace: 'nowrap',
                      maxWidth: '100px',
                      wordBreak: 'break-word',
                      borderRadius: '0 5px 5px 0',
                      paddingBottom: '0',
                      backgroundColor: 'transparent',
                      color: theme.palette.shades.lightDark,
                      paddingTop: column === 'Primary Color' || column === 'Secondary Color' ? '10px' : '0',
                    }}
                  >
                    {column}
                    {renderAITag()}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    );
  }
  return null;
}

export default GemAIInference;
